// =============================================================================
// Breadcrumb.jsx — a "Manage › Unleashed › SO Rules" trail under the header
// =============================================================================
// Shows where the current screen sits in the menu structure and lets you jump.
//   • A PAGE crumb (and every entry in a popover) is a real link → left-click does
//     in-app navigation (with the unsaved-changes guard); right-click / middle-click
//     / Ctrl-click opens it in a new tab natively.
//   • A GROUP crumb (a menu level like "Manage" / "Unleashed") opens a small popover
//     of that level's sibling pages to jump between (John, 26 Jul 2026).
//   • The last crumb is the current screen (plain, bold).
// Renders nothing on a top-level request screen or a non-nav path (empty trail).
//
// Props: page (current screen id) · onNavigate (fn(screenId) — the app's navigate).
// =============================================================================
import React from "react";
import { confirmNavAway } from "../services/navGuard";
import { breadcrumbTrailFor } from "./access";
import { useSession } from "./SessionContext.jsx";

export function Breadcrumb({ page, onNavigate }) {
  const { user } = useSession();
  const trail = React.useMemo(() => breadcrumbTrailFor(user, page), [user, page]);
  const [openIdx, setOpenIdx] = React.useState(null);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (openIdx == null) return;
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpenIdx(null);
    }
    function onKey(e) {
      if (e.key === "Escape") setOpenIdx(null);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [openIdx]);

  if (!trail || trail.length === 0) return null;

  // Left-click a link = in-app nav (guarded); modifier/middle click falls through
  // to the browser so it opens in a new tab.
  async function go(e, id) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
    e.preventDefault();
    setOpenIdx(null);
    if (!(await confirmNavAway())) return;
    onNavigate(id);
  }

  const linkCls =
    "no-underline text-zephyr-teal-dark hover:underline cursor-pointer bg-transparent border-none p-0 text-[13px]";
  const inheritFont = { fontFamily: "inherit" };

  return (
    <nav
      ref={ref}
      aria-label="Breadcrumb"
      className="max-w-[var(--page-max)] mx-auto px-6 py-[6px] flex items-center gap-[6px] text-[13px] text-zephyr-text/70"
    >
      {trail.map((crumb, idx) => {
        return (
          <React.Fragment key={crumb.label}>
            {idx > 0 && (
              <span aria-hidden="true" className="text-zephyr-text/40">
                ›
              </span>
            )}
            {crumb.current ? (
              <span className="font-semibold text-zephyr-text">{crumb.label}</span>
            ) : crumb.siblings ? (
              <span className="relative">
                <button
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={openIdx === idx}
                  onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                  className={linkCls}
                  style={inheritFont}
                >
                  {crumb.label}
                  <span className="text-[9px] ml-[2px]">▾</span>
                </button>
                {openIdx === idx && crumb.siblings.length > 0 && (
                  <div className="absolute left-0 top-[calc(100%+4px)] min-w-[190px] bg-white rounded-[8px] shadow-[0_4px_16px_rgba(0,0,0,0.2)] py-1 z-[105]">
                    {crumb.siblings.map((s) => (
                      <a
                        key={s.id}
                        href={`/${s.id}`}
                        onClick={(e) => go(e, s.id)}
                        style={inheritFont}
                        className={`block no-underline py-2 px-3 text-[13px] hover:bg-black/5 ${
                          s.id === page
                            ? "text-zephyr-text font-semibold bg-black/5"
                            : "text-zephyr-text"
                        }`}
                      >
                        {s.label}
                      </a>
                    ))}
                  </div>
                )}
              </span>
            ) : (
              <a
                href={`/${crumb.screen}`}
                onClick={(e) => go(e, crumb.screen)}
                className={linkCls}
                style={inheritFont}
              >
                {crumb.label}
              </a>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
